import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    runRekoSyncOut,
    runRekoBackup,
    runRekoBackupRestore,
    runRekoSyncIn,
    streamJobLogs,
    getJob,
} from "../../../lib/ligamanagerApi";
import { LEAGUE_OPTIONS } from "../../../lib/leagueSheetsID";

function prettyErr(e) {
    if (!e) return "Unbekannter Fehler";
    if (typeof e === "string") return e;
    if (e instanceof Error) return e.message || "Fehler";
    if (typeof e === "object") {
        if (e.detail) return typeof e.detail === "string" ? e.detail : JSON.stringify(e.detail, null, 2);
        if (e.message) return e.message;
        try {
            return JSON.stringify(e, null, 2);
        } catch {
            return String(e);
        }
    }
    return String(e);
}

function isDividerMsg(msg) {
    const s = String(msg || "").trim();
    if (!s) return false;
    return /^-+$/.test(s) && s.length >= 20;
}

function classifyLine(msg) {
    const s = String(msg || "");
    if (s.startsWith("[") && s.includes("]")) return "step";
    if (s.startsWith("    ")) return "indent";
    return "normal";
}

function downloadText(filename, text) {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function downloadJson(filename, obj) {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function ProgressBar({ percent, labelLeft, labelRight }) {
    const p = Math.max(0, Math.min(100, Number(percent || 0)));
    return (
        <div style={{ width: "100%", marginTop: 8 }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    opacity: 0.85,
                    fontFamily: "var(--ff-Everett)",
                    fontSize: 12,
                }}
            >
                <div>{labelLeft}</div>
                <div>{labelRight}</div>
            </div>

            <div
                style={{
                    marginTop: 6,
                    height: 10,
                    borderRadius: 999,
                    border: "1px solid rgba(232,253,83,.35)",
                    background: "rgba(232,253,83,.08)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        height: "100%",
                        width: `${p}%`,
                        background: "rgba(232,253,83,.75)",
                    }}
                />
            </div>
        </div>
    );
}

export default function RekoSyncPanel() {
    const [sheetIdOrUrl, setSheetIdOrUrl] = useState(LEAGUE_OPTIONS[0]?.value || "");
    const [tag, setTag] = useState("R2");

    const [jobId, setJobId] = useState(null);
    const [runningAction, setRunningAction] = useState("");
    const stopStreamRef = useRef(null);

    const [logItems, setLogItems] = useState([]);
    const [error, setError] = useState("");

    const [actionBusy, setActionBusy] = useState(false);

    const [showDetails, setShowDetails] = useState(false);
    const [autoScroll, setAutoScroll] = useState(true);
    const [filterInfo, setFilterInfo] = useState(true);
    const [filterWarn, setFilterWarn] = useState(true);
    const [filterError, setFilterError] = useState(true);
    const [filterUi, setFilterUi] = useState(true);

    const [progress, setProgress] = useState({
        status: "",
        step: 0,
        total: 0,
        label: "",
    });

    const jobPollTimerRef = useRef(null);
    const scrollBoxRef = useRef(null);
    const stickToBottomRef = useRef(true);

    const busy = actionBusy;

    function pushUiInfo(msg) {
        setLogItems((prev) => [
            ...prev,
            { seq: null, level: "INFO", ts: null, msg: String(msg || ""), uiOnly: true },
        ]);
    }

    function pushLogItem(item) {
        let level = (item?.level || "INFO").toUpperCase();
        if (!["INFO", "WARN", "ERROR"].includes(level)) level = "INFO";

        setLogItems((prev) => [
            ...prev,
            {
                seq: typeof item?.seq === "number" ? item.seq : null,
                level,
                ts: item?.ts ?? null,
                msg: item?.msg ?? "",
                uiOnly: false,
            },
        ]);
    }

    function stopStream() {
        try {
            if (stopStreamRef.current) stopStreamRef.current();
        } catch {}
        stopStreamRef.current = null;
    }

    function stopJobPolling() {
        if (jobPollTimerRef.current) {
            clearInterval(jobPollTimerRef.current);
            jobPollTimerRef.current = null;
        }
    }

    useEffect(() => {
        return () => {
            stopStream();
            stopJobPolling();
        };
    }, []);

    useEffect(() => {
        const el = scrollBoxRef.current;
        if (!el || !autoScroll || !stickToBottomRef.current) return;
        el.scrollTop = el.scrollHeight;
    }, [logItems, autoScroll]);

    function onScrollLogs() {
        const el = scrollBoxRef.current;
        if (!el) return;
        const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 40;
        stickToBottomRef.current = nearBottom;
    }

    function resetRunState() {
        setError("");
        setLogItems([]);
        setJobId(null);
        setRunningAction("");
        setProgress({ status: "", step: 0, total: 0, label: "" });
        stopStream();
        stopJobPolling();
    }

    function startJobPolling(id) {
        stopJobPolling();

        const pollOnce = async () => {
            try {
                const j = await getJob(id);
                setProgress({
                    status: j?.status || "",
                    step: Number(j?.progress_step || 0),
                    total: Number(j?.progress_total || 0),
                    label: j?.progress_label || "",
                });

                if (j?.status && ["done", "error", "canceled"].includes(String(j.status))) {
                    stopJobPolling();
                }
            } catch {
                // polling silently continues
            }
        };

        pollOnce();
        jobPollTimerRef.current = setInterval(pollOnce, 700);
    }

    async function startAction(action) {
        resetRunState();

        if (!sheetIdOrUrl.trim()) return setError("Bitte Sheet ID/URL eintragen.");
        if (!tag.trim()) return setError("Bitte Renntag angeben, z. B. R2.");

        setActionBusy(true);
        setRunningAction(action);

        try {
            pushUiInfo(`Aktion wird gestartet: ${action}`);

            const payload = {
                sheetInput: sheetIdOrUrl.trim(),
                tag: tag.trim().toUpperCase(),
            };

            let r;
            if (action === "reko-sync-out") {
                r = await runRekoSyncOut(payload);
            } else if (action === "reko-backup") {
                r = await runRekoBackup(payload);
            } else if (action === "reko-backup-restore") {
                r = await runRekoBackupRestore(payload);
            } else if (action === "reko-sync-in") {
                r = await runRekoSyncIn(payload);
            } else {
                throw new Error(`Unbekannte Aktion: ${action}`);
            }

            if (!r?.job_id) {
                throw new Error(`Backend hat kein job_id zurückgegeben (${action}).`);
            }

            setJobId(r.job_id);
            pushUiInfo(`Job gestartet: #${r.job_id}`);
            pushUiInfo("Live Logs verbinden …");

            startJobPolling(r.job_id);

            const stopFn = streamJobLogs({
                job_id: r.job_id,
                onLog: (item) => pushLogItem(item),
                onDone: (status) => {
                    pushUiInfo(`Job beendet: ${status}`);
                    if (String(status).trim() === "error") {
                        setError("Job beendet mit Fehler. Siehe Logs.");
                    }
                    setActionBusy(false);
                    stopStream();
                    stopJobPolling();
                },
                onError: (e) => {
                    const msg = prettyErr(e);
                    pushUiInfo(`SSE FEHLER: ${msg}`);
                    setError(msg);
                    setActionBusy(false);
                    stopStream();
                    stopJobPolling();
                },
            });

            stopStreamRef.current = stopFn;
        } catch (e) {
            const msg = prettyErr(e);
            setError(msg);
            pushUiInfo(`FEHLER: ${msg}`);
            setActionBusy(false);
            stopStream();
            stopJobPolling();
        }
    }

    function onClearLogs() {
        setLogItems([]);
        setError("");
    }

    function onDownloadJson() {
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const fname = `racevision_reko_${jobId || "nojob"}_logs_${ts}.json`;

        downloadJson(fname, {
            job_id: jobId,
            running_action: runningAction,
            tag,
            sheet_input: sheetIdOrUrl,
            logs: logItems,
            progress,
        });
    }

    function onDownloadTxt() {
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const fname = `racevision_reko_${jobId || "nojob"}_logs_${ts}.txt`;

        const lines = logItems.map((it) => {
            if (!showDetails) return String(it.msg ?? "");
            return JSON.stringify(
                { seq: it.seq, level: it.level, ts: it.ts, msg: it.msg, uiOnly: it.uiOnly },
                null,
                0
            );
        });

        downloadText(fname, lines.join("\n"));
    }

    const renderedItems = useMemo(() => {
        const allowLevel = (lvl, uiOnly) => {
            const L = (lvl || "INFO").toUpperCase();
            if (uiOnly) return !!filterUi;
            if (L === "INFO") return !!filterInfo;
            if (L === "WARN") return !!filterWarn;
            if (L === "ERROR") return !!filterError;
            return true;
        };

        return logItems
            .filter((it) => allowLevel(it.level, !!it.uiOnly))
            .map((it, idx) => {
                const msg = String(it.msg ?? "");
                let level = (it.level || "INFO").toUpperCase();
                if (!["INFO", "WARN", "ERROR"].includes(level)) level = "INFO";

                return {
                    key: `${it.seq ?? "x"}-${idx}`,
                    level,
                    ts: it.ts,
                    seq: it.seq,
                    msg,
                    isDivider: isDividerMsg(msg),
                    kind: classifyLine(msg),
                    uiOnly: !!it.uiOnly,
                    rawJson: JSON.stringify(
                        { seq: it.seq, level, ts: it.ts, msg: it.msg, uiOnly: it.uiOnly },
                        null,
                        0
                    ),
                };
            });
    }, [logItems, filterInfo, filterWarn, filterError, filterUi]);

    const filteredCount = renderedItems.length;
    const totalCount = logItems.length;
    const progressPercent =
        progress.total > 0 ? Math.round((Math.max(0, progress.step) / Math.max(1, progress.total)) * 100) : 0;

    return (
        <div className="card card--withbar">
            <h2 style={{ marginTop: 0 }}>Reko Sync / Backup</h2>

            <div style={{ display: "grid", gap: 12, width: "100%", maxWidth: 980 }}>
                <div className="callout">
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Aktionen</div>
                    <div style={{ display: "grid", gap: 6, fontFamily: "var(--ff-Everett)" }}>
                        <div><b>Reko Sync OUT</b>: kopiert <code>{tag || "R2"}_Incidents</code> aus dem Main Sheet in die Reko-Sheets.</div>
                        <div><b>Backup</b>: sichert Vorrennen, Standings und Lizenz ins feste Backup-Sheet.</div>
                        <div><b>Backup Restore</b>: schreibt diese Sicherung zurück ins Main Sheet.</div>
                        <div><b>Reko Sync IN</b>: übernimmt ReKo-Daten zurück ins Main Sheet, wendet Strafen an und rechnet Punkte/Standings neu.</div>
                    </div>
                </div>

                <label style={{ width: "100%" }}>
                    <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                        Liga
                    </div>
                    <select
                        className="input"
                        value={sheetIdOrUrl}
                        onChange={(e) => setSheetIdOrUrl(e.target.value)}
                        style={{ width: 260 }}
                        disabled={busy}
                    >
                        {LEAGUE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </label>

                <label>
                    <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                        Renntag
                    </div>
                    <input
                        className="input"
                        value={tag}
                        onChange={(e) => setTag((e.target.value || "").toUpperCase())}
                        placeholder="R2"
                        style={{ width: 160 }}
                        disabled={busy}
                    />
                </label>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                        className="button button--secondary"
                        onClick={() => startAction("reko-sync-out")}
                        disabled={busy || !sheetIdOrUrl.trim() || !tag.trim()}
                    >
                        1) Reko Sync OUT
                    </button>

                    <button
                        className="button button--secondary"
                        onClick={() => startAction("reko-backup")}
                        disabled={busy || !sheetIdOrUrl.trim() || !tag.trim()}
                    >
                        {busy && runningAction === "reko-backup" ? "Backup…" : "2) Backup"}
                    </button>

                    <button
                        className="button button--secondary"
                        onClick={() => startAction("reko-backup-restore")}
                        disabled={busy || !sheetIdOrUrl.trim() || !tag.trim()}
                    >
                        {busy && runningAction === "reko-backup-restore" ? "Restore…" : "3) Backup Restore"}
                    </button>
                    <button
                        className="button button--secondary"
                        onClick={() => startAction("reko-sync-in")}
                        disabled={busy || !sheetIdOrUrl.trim() || !tag.trim()}
                    >
                        {busy && runningAction === "reko-sync-in" ? "Reko Sync IN…" : "4) Reko Sync IN"}
                    </button>
                </div>

                {jobId && (
                    <div style={{ opacity: 0.9, fontFamily: "var(--ff-Everett)" }}>
                        Job: <b>#{jobId}</b> {runningAction ? <>– Aktion: <code>{runningAction}</code></> : null}
                    </div>
                )}

                {jobId ? (
                    <ProgressBar
                        percent={progressPercent}
                        labelLeft={
                            progress.label
                                ? `${progress.label} (${progress.status || "..."})`
                                : progress.status
                                    ? `Status: ${progress.status}`
                                    : "Status: …"
                        }
                        labelRight={progress.total > 0 ? `${progress.step}/${progress.total} · ${progressPercent}%` : ""}
                    />
                ) : null}

                {error && <div className="error-box">{error}</div>}

                <div style={{ marginTop: 10 }}>
                    <div
                        style={{
                            height: 2,
                            width: "100%",
                            borderRadius: 999,
                            background: "rgba(232,253,83,.65)",
                            boxShadow: "0 0 0 1px rgba(232,253,83,.18)",
                            marginBottom: 10,
                        }}
                    />

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <div style={{ opacity: 0.9, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                            Logs
                        </div>
                        <div style={{ opacity: 0.7, fontFamily: "var(--ff-Everett)", fontSize: 12 }}>
                            {totalCount ? `${filteredCount}/${totalCount} Zeilen` : ""}
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10, alignItems: "center" }}>
                        <button className="button button--secondary" onClick={stopStream} disabled={!stopStreamRef.current}>
                            Logs stoppen
                        </button>
                        <button className="button button--secondary" onClick={onClearLogs} disabled={totalCount === 0}>
                            Clear Logs
                        </button>
                        <button className="button button--secondary" onClick={onDownloadTxt} disabled={totalCount === 0}>
                            Download TXT
                        </button>
                        <button className="button button--secondary" onClick={onDownloadJson} disabled={totalCount === 0}>
                            Download JSON
                        </button>
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", opacity: 0.95, marginBottom: 10 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input type="checkbox" checked={showDetails} onChange={(e) => setShowDetails(e.target.checked)} />
                            <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>Details anzeigen</span>
                        </label>

                        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} />
                            <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>Auto-Scroll</span>
                        </label>

                        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input type="checkbox" checked={filterUi} onChange={(e) => setFilterUi(e.target.checked)} />
                            <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>UI</span>
                        </label>

                        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input type="checkbox" checked={filterInfo} onChange={(e) => setFilterInfo(e.target.checked)} />
                            <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>INFO</span>
                        </label>

                        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input type="checkbox" checked={filterWarn} onChange={(e) => setFilterWarn(e.target.checked)} />
                            <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>WARN</span>
                        </label>

                        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input type="checkbox" checked={filterError} onChange={(e) => setFilterError(e.target.checked)} />
                            <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>ERROR</span>
                        </label>
                    </div>

                    <div
                        ref={scrollBoxRef}
                        onScroll={onScrollLogs}
                        className="logbox"
                        style={{
                            minHeight: 240,
                            maxHeight: 520,
                            overflow: "auto",
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {renderedItems.length === 0 ? (
                            <div style={{ opacity: 0.65 }}>Noch keine Logs vorhanden.</div>
                        ) : (
                            renderedItems.map((it) => {
                                if (it.isDivider) {
                                    return (
                                        <div key={it.key} style={{ opacity: 0.45, margin: "6px 0" }}>
                                            {it.msg}
                                        </div>
                                    );
                                }

                                let color = "var(--text)";
                                if (it.level === "WARN") color = "var(--warn)";
                                if (it.level === "ERROR") color = "var(--err)";
                                if (it.uiOnly) color = "var(--accent)";

                                return (
                                    <div
                                        key={it.key}
                                        style={{
                                            color,
                                            paddingLeft: it.kind === "indent" ? 14 : 0,
                                            marginBottom: 4,
                                            fontFamily: "var(--ff-Everett)",
                                        }}
                                        title={showDetails ? it.rawJson : undefined}
                                    >
                                        {showDetails && !it.uiOnly ? `[${it.level}] ` : ""}
                                        {it.msg}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}